import { motion } from "motion/react";
import { DURATION, EASE } from "@/components/motion/motion";
import { cn } from "@/lib/utils";

/** Night background: blueprint grid plus a faint signal glow. */
export function NightDecor({ glow = "right" }: { glow?: "right" | "center" }) {
	return (
		<>
			<div className="blueprint absolute inset-0" />
			<div
				className={cn(
					"absolute size-[46rem] rounded-full bg-(--glow) blur-[140px]",
					glow === "right"
						? "-top-40 -right-40"
						: "-bottom-[30rem] left-1/2 -translate-x-1/2",
				)}
			/>
		</>
	);
}

/**
 * The brand trace: the "L" of the mark drawn as a long route, vertical
 * (understand) then horizontal (deliver). Draws itself once in view.
 */
export function LezoTrace({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 100 240"
			fill="none"
			aria-hidden="true"
			className={cn("overflow-visible", className)}
			preserveAspectRatio="none"
		>
			<path
				d="M8 0 V200 Q8 232 40 232 H100"
				className="stroke-grid-line"
				strokeWidth="2"
				vectorEffect="non-scaling-stroke"
			/>
			<motion.path
				d="M8 0 V200 Q8 232 40 232 H100"
				className="stroke-signal"
				strokeWidth="2"
				strokeLinecap="round"
				vectorEffect="non-scaling-stroke"
				initial={{ pathLength: 0 }}
				whileInView={{ pathLength: 1 }}
				viewport={{ once: true, margin: "0px 0px -20% 0px" }}
				transition={{ duration: DURATION.draw, ease: EASE }}
			/>
		</svg>
	);
}

/** Endless strip of words, pure CSS. Decorative: the list is in the page. */
export function Marquee({
	items,
	className,
}: {
	items: string[];
	className?: string;
}) {
	const row = (hidden: boolean) => (
		<ul
			aria-hidden={hidden || undefined}
			className="flex shrink-0 items-center gap-10 pr-10"
		>
			{items.map((item) => (
				<li
					key={item}
					className="flex items-center gap-10 font-mono text-base tracking-[-0.03em] whitespace-nowrap text-muted-foreground md:text-lg"
				>
					{item}
					<span aria-hidden="true" className="text-signal-ink">
						/
					</span>
				</li>
			))}
		</ul>
	);

	return (
		<div
			className={cn("relative flex overflow-hidden", className)}
			style={{
				maskImage:
					"linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
			}}
		>
			<div className="marquee-track flex w-max">
				{row(false)}
				{row(true)}
			</div>
		</div>
	);
}
